import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Card,
  CardContent,
  Collapse,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import AdminPage from "./AdminPage";
import { apiClient } from "../utils/apiClient";
import { DatabaseObjectView } from "./DatabaseObjectView";

interface FeedbackInfo {
  id: string;
  createdAt: string;
  updatedAt: string;
  feedbackText: string;
  messageId: string;
  destinationEmail: string;
  dobbyActionId?: string;
  userId?: string | null;
  userEmail?: string | null;
  userName?: string | null;
}

interface FeedbackListResponse {
  feedbacks: FeedbackInfo[];
  total: number;
  hasMore: boolean;
}

interface ExpandedRow {
  [key: string]: boolean;
}

export function AdminFeedbackPage() {
  const [feedbacks, setFeedbacks] = useState<FeedbackInfo[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [expandedRows, setExpandedRows] = useState<ExpandedRow>({});
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(
    null
  );
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const fetchFeedbacks = useCallback(
    async (offset: number = 0, limit: number = 50) => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiClient.post<FeedbackListResponse>(
          "/feedback/list",
          {
            offset,
            limit,
            sortBy: "createdAt",
            sortOrder: "desc",
          }
        );

        if (offset === 0) {
          setFeedbacks(response.feedbacks);
        } else {
          setFeedbacks((prev) => [...prev, ...response.feedbacks]);
        }

        setTotal(response.total);
        setHasMore(response.hasMore);
      } catch (err) {
        console.error("Error fetching feedbacks:", err);
        setError("Failed to load feedbacks");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchFeedbacks();
  }, [fetchFeedbacks]);

  const handleExpandRow = (id: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleViewDetails = (feedback: FeedbackInfo) => {
    setSelectedFeedbackId(feedback.id);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedFeedbackId(null);
  };

  const handleLoadMore = () => {
    fetchFeedbacks(feedbacks.length, 50);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <AdminPage>
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4" component="h1">
            User Feedback
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchFeedbacks()}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Stack direction="row" spacing={3}>
              <Box>
                <Typography variant="h4" color="primary">
                  {total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Feedback
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" color="secondary">
                  {feedbacks.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Loaded
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Created</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Feedback</TableCell>
                <TableCell>Destination Email</TableCell>
                <TableCell>Message ID</TableCell>
                <TableCell>Action ID</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feedbacks.map((feedback) => (
                <React.Fragment key={feedback.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleExpandRow(feedback.id)}
                      >
                        {expandedRows[feedback.id] ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(feedback.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {feedback.userEmail ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {feedback.userName || "Unknown"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            {feedback.userEmail}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip
                          label="Anonymous"
                          size="small"
                          color="default"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {truncateText(feedback.feedbackText, 80)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                      >
                        {feedback.destinationEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                      >
                        {truncateText(feedback.messageId, 20)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {feedback.dobbyActionId ? (
                        <Chip
                          label={truncateText(feedback.dobbyActionId, 8)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(feedback)}
                        title="View Details"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      style={{ paddingBottom: 0, paddingTop: 0 }}
                      colSpan={8}
                    >
                      <Collapse
                        in={expandedRows[feedback.id]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ margin: 2 }}>
                          <Typography variant="h6" gutterBottom component="div">
                            Full Feedback
                          </Typography>
                          <Paper sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
                            <Typography
                              variant="body1"
                              style={{ whiteSpace: "pre-wrap" }}
                            >
                              {feedback.feedbackText}
                            </Typography>
                          </Paper>
                          <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                            <Typography variant="body2">
                              <strong>Updated:</strong>{" "}
                              {formatDate(feedback.updatedAt)}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Full Message ID:</strong>{" "}
                              <span
                                style={{
                                  fontFamily: "monospace",
                                  fontSize: "0.8rem",
                                }}
                              >
                                {feedback.messageId}
                              </span>
                            </Typography>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && feedbacks.length === 0 && (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Typography variant="h6" color="text.secondary">
              No feedback found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              When users report issues with email routing, they'll appear here.
            </Typography>
          </Box>
        )}

        {hasMore && !loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <Button variant="outlined" onClick={handleLoadMore}>
              Load More
            </Button>
          </Box>
        )}

        {/* Detail Dialog using DatabaseObjectView */}
        <DatabaseObjectView
          open={detailDialogOpen}
          onClose={handleCloseDetailDialog}
          initialTable="userFeedback"
          initialId={selectedFeedbackId || ""}
          autoFetch={true}
        />
      </Box>
    </AdminPage>
  );
}
